import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import type {
  ShopOverrideRuleDto,
  ShopOverrideRuleActionDto,
  CreateShopOverrideRuleDto,
  UpdateShopOverrideRuleDto,
} from "@babackapp/private-api-client/types";
import {
  FieldIdDefinition,
  type OverrideRuleFormValuesInput,
} from "@/lib/override-rules";
import { OverrideRuleForm } from "./override-rule-form";
import { useOverrideRules } from "@/components/override-rules/override-rules-context.tsx";
import useTranslation from "@babackapp/translations/use-translation";

interface OverrideRuleDialogLabels {
  createTitle?: string;
  editTitle?: string;
}

interface OverrideRuleDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  existingRule?: ShopOverrideRuleDto;
  actionType: ShopOverrideRuleActionDto["type"];
  actionValueOptions?: { label: string; key: string }[];
  onSuccess?: (rules: ShopOverrideRuleDto[]) => void;
  excludeFields?: FieldIdDefinition[];
  labels?: OverrideRuleDialogLabels;
}

export function OverrideRuleDialog({
  isOpen,
  onOpenChange,
  existingRule,
  actionType,
  actionValueOptions,
  excludeFields,
  labels,
}: OverrideRuleDialogProps) {
  const t = useTranslation();
  const {
    rules,
    createRule,
    createRulePending,
    updateRules,
    updateRulesPending,
  } = useOverrideRules();
  const isEditing = !!existingRule;

  const isLoading = createRulePending || updateRulesPending;

  const handleSubmit = (data: OverrideRuleFormValuesInput) => {
    if (isEditing && existingRule) {
      const updateData: UpdateShopOverrideRuleDto = {
        id: existingRule.id,
        name: data.name,
        description: data.description,
        isActive: data.isActive ?? true,
        priority: existingRule.priority,
        conditions: data.conditions,
        action: data.action,
      };
      updateRules([updateData], { onSuccess: () => onOpenChange(false) });
    } else {
      const createData: CreateShopOverrideRuleDto = {
        name: data.name,
        description: data.description,
        isActive: data.isActive ?? true,
        priority:
          rules.filter((rule) => rule.action?.type === actionType).length + 1,
        conditions: data.conditions,
        action: data.action,
      };
      createRule(createData, { onSuccess: () => onOpenChange(false) });
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? (labels?.editTitle ?? t("overrideRules.misc.editTitle"))
              : (labels?.createTitle ?? t("overrideRules.misc.createTitle"))}
          </DialogTitle>
        </DialogHeader>

        <OverrideRuleForm
          existingRule={existingRule}
          actionType={actionType}
          actionValueOptions={actionValueOptions}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isLoading}
          excludeFields={excludeFields}
        />
      </DialogContent>
    </Dialog>
  );
}
